package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.theme.DisplayTitleStyle
import com.htmlagent.site.theme.EyebrowStyle
import com.htmlagent.site.theme.LandingBandStyle
import com.htmlagent.site.theme.LandingInnerStyle
import com.htmlagent.site.theme.LandingLeadStyle
import com.htmlagent.site.theme.LandingSectionStyle
import com.htmlagent.site.theme.SectionHeadStyle
import com.htmlagent.site.theme.TitleRuleStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * How far apart the cards of a row arrive, in milliseconds.
 *
 * Small enough that the row still reads as one gesture: past about 120 ms the last card looks
 * like it is loading rather than arriving.
 */
const val STAGGER_MS: Int = 90

/**
 * One band of the page: a full-bleed strip with its own vertical air and a centred column.
 *
 * [ink] is what gives the page its beat. It does not paint a new palette: it re-resolves the
 * tokens the dark way for this subtree, so everything inside is written exactly as it would be
 * outside and lands on values already calibrated for a dark surface.
 *
 * The [id] is the anchor the navigation bar points at, so every band has one whether or not the
 * bar links to it today.
 */
@Composable
fun Band(id: String, ink: Boolean = false, content: @Composable () -> Unit) {
    val band = if (ink) LandingBandStyle.toModifier() else Modifier
    Section(
        band.then(LandingSectionStyle.toModifier()).toAttrs {
            attr("id", id)
            attr("aria-labelledby", headingId(id))
        },
    ) {
        Div(LandingInnerStyle.toModifier().toAttrs()) { content() }
    }
}

/**
 * The id of the heading that names a band, derived from the band's own.
 *
 * A `<section>` with no accessible name is not a landmark at all: it vanishes from the region
 * list a screen reader offers, so the page has no table of contents even though every band on it
 * carries a headline. Pointing each section at its own heading is what turns the six of them into
 * six places you can jump to, and it costs one derived id rather than a second copy of the title.
 */
fun headingId(section: String): String = "$section-heading"

/**
 * The centred header of a band: label, headline, rule, and an optional sentence.
 *
 * The rule is `aria-hidden`: it separates the headline from what follows for the eye, and read
 * aloud it would be an interruption with nothing in it.
 */
@Composable
fun SectionHead(
    eyebrow: String,
    title: String,
    ink: Boolean = false,
    lead: String? = null,
    headingId: String? = null,
) {
    Div(SectionHeadStyle.toModifier().reveal().toAttrs()) {
        Span(EyebrowStyle.toModifier().toAttrs { if (ink) attr("data-tone", "accent") }) {
            Text(eyebrow)
        }
        H2(DisplayTitleStyle.toModifier().toAttrs { headingId?.let { attr("id", it) } }) {
            Text(title)
        }
        Div(TitleRuleStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
        lead?.let {
            P(LandingLeadStyle.toModifier().toAttrs { attr("data-align", "center") }) { Text(it) }
        }
    }
}
